// ────────────────────────────────────────────────────────────────────────────
// Local notifications — declarative on-device reminders.
//
// Each reminder is ONE entry in the REMINDERS registry below.
//   • To ADD a reminder: append an entry (unique id + its key in the API's
//     notification catalogue + when it fires + 2 i18n keys). "When" is either
//     `days: N` — a countdown restarted on every app open — or
//     `endOfMonth: true`, the real last day of the month.
//   • To REMOVE one:      delete the entry (or set `enabled: false`).
// No other code changes are needed — the app lifecycle calls rearmReminders(),
// which simply loops over the registry.
//
// The registry is the copy shipped with the app: what runs on first launch and
// offline. Once synced (GET /notifications/config, see lib/notifications), the
// admin's on/off, days, hour and texts replace it, key by key; a reminder the
// admin turns off leaves the phone at the next app opening.
//
// These are LOCAL notifications (scheduled on the device), distinct from the FCM
// push in src/lib/push.js. They re-arm on every app open, so a "N days without
// opening the app" reminder resets its countdown each time the app is opened.
//
// Scheduling is NATIVE ONLY; the config also controls the in-page protocol sound.
// Permission is NOT requested here: it is already
// requested by the push flow at login; we only schedule when it has been granted
// (on iOS/Android a single OS notification permission covers push + local).
// ────────────────────────────────────────────────────────────────────────────
import { Capacitor } from '@capacitor/core'
import { LocalNotifications } from '@capacitor/local-notifications'
import { i18n, loadTranslations } from '@/i18n'
import { nextFireDate } from '@/lib/helpers/reminder-schedule'
import { mergeReminder } from '@/lib/notifications/config'
import { loadPhoneConfig, syncPhoneConfig } from '@/lib/notifications/phone-config'
import store from '@/store'

// ➕ Add / remove reminders here. Each entry is fully self-contained.
const REMINDERS = [
    {
        id: 1001, // stable, unique integer (the OS notification id)
        key: 'reminder.inactivity_7d', // its key in the API's notification catalogue
        enabled: true, // flip to false (or delete the entry) to disable
        days: 7, // fires this many days after the last time the app was opened
        hour: 19, // friendly local fire time
        minute: 0,
        titleKey: 'notification.inactivity7d.title',
        bodyKey: 'notification.inactivity7d.body',
    },
    {
        id: 1002,
        key: 'reminder.month_end',
        enabled: true,
        endOfMonth: true, // the real last day: 31, 30, 29 or 28 as the month has it
        // Morning, not evening: the monthly report is built on the 1st at 09:00,
        // so this leaves a full day to sync rather than a single night.
        hour: 10,
        minute: 0,
        titleKey: 'notification.monthEnd.title',
        bodyKey: 'notification.monthEnd.body',
    },
]

export function notificationsSupported() {
    return Capacitor.getPlatform() !== 'web'
}

async function isGranted() {
    try {
        const perm = await LocalNotifications.checkPermissions()
        return perm.display === 'granted'
    } catch {
        return false
    }
}

async function cancelReminder(reminder) {
    try {
        await LocalNotifications.cancel({ notifications: [{ id: reminder.id }] })
    } catch {
        // nothing scheduled with that id — safe to ignore
    }
}

async function scheduleReminder(reminder) {
    try {
        await cancelReminder(reminder) // avoid stacking duplicates
        await LocalNotifications.schedule({
            notifications: [
                {
                    id: reminder.id,
                    title: reminder.title ?? i18n.global.t(reminder.titleKey),
                    body: reminder.body ?? i18n.global.t(reminder.bodyKey),
                    // A reminder does not need to fire to the minute. Exact is the plugin
                    // default since 8.3.0, and on Android 12 it throws the user out
                    // to the system alarm settings on every schedule() call.
                    isExactNotification: false,
                    schedule: { at: nextFireDate(reminder), allowWhileIdle: true },
                },
            ],
        })
    } catch (e) {
        console.warn('local reminder schedule failed', reminder.key, e)
    }
}

/**
 * Re-arm every enabled reminder. Call on app boot AND on every foreground so the
 * countdown (e.g. 7 days of inactivity) is pushed back each time the app opens.
 * Config sync is independent of OS permission. Scheduling is native-only and
 * requires permission (the push flow at login requests it). Never throws.
 */
export async function rearmReminders() {
    try {
        // Preferences also govern the in-page protocol sound, even when the OS
        // refuses notifications (or on web). Sync before checking permission.
        const config = store.getters['auth/loggedIn'] ? await syncPhoneConfig() : await loadPhoneConfig()
        if (!notificationsSupported()) return
        // Ensure the locale messages are loaded before t(): rearm can run at boot
        // before i18n finishes lazy-loading, which would schedule raw key strings.
        await loadTranslations(i18n.global.locale.value)
        if (!(await isGranted())) {
            // Not granted yet → clear anything dangling. The push permission prompt
            // (at login) will unlock scheduling on a later foreground.
            await Promise.all(REMINDERS.map(cancelReminder))
            return
        }
        for (const reminder of REMINDERS.map((entry) => mergeReminder(entry, config))) {
            if (reminder.enabled) await scheduleReminder(reminder)
            else await cancelReminder(reminder)
        }
    } catch (e) {
        console.warn('rearmReminders failed', e)
    }
}
